import { useEffect, useState } from 'react';
import prodApi from '../../services/prodApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Plus, Pencil, Trash2, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { SortableRow, useSortableTable, SortableTableWrapper } from '../../components/produccion/SortableTable';


export const Entalles = () => {
  const [items, setItems] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', tipo_ids: [], orden: 0 });
  const [popoverOpen, setPopoverOpen] = useState(false);

  const { sensors, handleDragEnd, isSaving, modifiers } = useSortableTable(items, setItems, 'entalles');

  const fetchItems = async () => {
    try {
      const response = await prodApi.get(`/entalles`);
      setItems(response.data);
    } catch (error) {
      toast.error('Error al cargar entalles');
    } finally {
      setLoading(false);
    }
  };

  const fetchTipos = async () => {
    try {
      const response = await prodApi.get(`/tipos`);
      setTipos(response.data);
    } catch (error) {
      console.error('Error fetching tipos:', error);
    }
  };

  useEffect(() => {
    fetchItems();
    fetchTipos();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await prodApi.put(`/entalles/${editingItem.id}`, formData);
        toast.success('Entalle actualizado');
      } else {
        await prodApi.post(`/entalles`, formData);
        toast.success('Entalle creado');
      }
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({ nombre: '', tipo_ids: [], orden: 0 });
      fetchItems();
    } catch (error) {
      toast.error('Error al guardar entalle');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ nombre: item.nombre, tipo_ids: item.tipo_ids || [], orden: item.orden || 0 });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await prodApi.delete(`/entalles/${id}`);
      toast.success('Entalle eliminado');
      fetchItems();
    } catch (error) {
      toast.error('Error al eliminar entalle');
    }
  };

  const handleNew = () => {
    setEditingItem(null);
    setFormData({ nombre: '', tipo_ids: [], orden: 0 });
    setDialogOpen(true);
  };

  const toggleTipo = (tipoId) => {
    const current = formData.tipo_ids || [];
    if (current.includes(tipoId)) {
      setFormData({ ...formData, tipo_ids: current.filter(id => id !== tipoId) });
    } else {
      setFormData({ ...formData, tipo_ids: [...current, tipoId] });
    }
  };

  return (
    <div className="space-y-6" data-testid="entalles-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Entalles</h2>
          <p className="text-muted-foreground">
            Gestión de entalles de productos
            {isSaving && <span className="ml-2 text-xs">(Guardando...)</span>}
          </p>
        </div>
        <Button onClick={handleNew} data-testid="btn-nuevo-entalle">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Entalle
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="data-table-header">
                <TableHead className="w-[40px]"></TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Tipos</TableHead>
                <TableHead className="w-[100px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    No hay entalles registrados
                  </TableCell>
                </TableRow>
              ) : (
                <SortableTableWrapper
                  items={items}
                  sensors={sensors}
                  handleDragEnd={handleDragEnd}
                  modifiers={modifiers}
                >
                  {items.map((item) => (
                    <SortableRow key={item.id} id={item.id}>
                      <TableCell className="font-medium">{item.nombre}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(item.tipo_ids || []).map(tipoId => {
                            const tipo = tipos.find(t => t.id === tipoId);
                            return tipo ? (
                              <Badge key={tipoId} variant="secondary" className="text-xs">{tipo.nombre}</Badge>
                            ) : null;
                          })}
                          {(!item.tipo_ids || item.tipo_ids.length === 0) && (
                            <span className="text-muted-foreground text-sm">Sin tipos</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </SortableRow>
                  ))}
                </SortableTableWrapper>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Entalle' : 'Nuevo Entalle'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Modifica los datos del entalle' : 'Agrega un nuevo entalle al catálogo'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Nombre del entalle"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Tipos disponibles</Label>
                <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between h-auto min-h-10">
                      <div className="flex flex-wrap gap-1 flex-1">
                        {formData.tipo_ids?.length === 0 ? (
                          <span className="text-muted-foreground">Seleccionar tipos...</span>
                        ) : (
                          formData.tipo_ids.map(id => {
                            const tipo = tipos.find(t => t.id === id);
                            return tipo ? (
                              <Badge key={id} variant="secondary" className="text-xs">{tipo.nombre}</Badge>
                            ) : null;
                          })
                        )}
                      </div>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar tipo..." />
                      <CommandList>
                        <CommandEmpty>No se encontraron tipos.</CommandEmpty>
                        <CommandGroup>
                          {tipos.map((tipo) => (
                            <CommandItem key={tipo.id} value={tipo.nombre} onSelect={() => toggleTipo(tipo.id)}>
                              <div className={cn(
                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                                formData.tipo_ids?.includes(tipo.id) ? "bg-primary border-primary text-primary-foreground" : "opacity-50"
                              )}>
                                {formData.tipo_ids?.includes(tipo.id) && <Check className="h-3 w-3" />}
                              </div>
                              {tipo.nombre}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">{editingItem ? 'Actualizar' : 'Crear'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default Entalles;
